import React from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';

import { CloseIcon } from '../../Icon';

// Import HTML content
import { pleatTypeContent } from './pleatTypeContent';
import { liningContent } from './liningContent';
import { stackContent } from './stackContent';
import { measurementContent } from './measurementContent';

const StyledWrapper = styled.div`
  position: relative;
`;

const StyledCloseIconWrapper = styled.span`
  width: 30px;
  height: 30px;
  top: -2rem;
  left: -2rem;
  padding: 4px;
  position: absolute;
  z-index: 99;
  background: #327388;
  display: inline-block;
  border-radius: 50%;
  cursor: pointer;
`;

const StyledContent = styled.div`
  font: normal normal 300 14px/20px Lato;
  letter-spacing: 0.28px;
  color: #242325;
  margin: 0 auto 2rem;
  min-height: 20px;
  
  /* Style the imported HTML content */
  .type-of-fold {
    .heading {
      margin-bottom: 30px;
      
      h4 {
        font-size: 20px;
        margin: 0 0 16px;
        padding: 0 0 16px;
        border-bottom: 1px solid #efefef;
        text-align: center;
        color: #327388;
      }
      
      p {
        text-align: center;
        margin: 0 0 16px;
      }
      
      li {
        position: relative;
        list-style: none;
        padding: 0 0 0 24px;
        margin: 0;
      }
      
      ul {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      
      li:before {
        content: "";
        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M6 11L9.5 14.5L17.5 6.5" stroke="%234BAD3C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>');
        position: absolute;
        top: 1px;
        width: 18px;
        height: 18px;
        background-size: 18px;
        left: 0;
      }
    }
    
    .row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 40px;
      
      @media (max-width: 767px) {
        grid-template-columns: 1fr;
      }
    }
    
    .type-of-fold-item {
      h5 {
        margin: 0 0 24px;
        font-size: 18px;
        color: #327388;
      }
      
      img {
        width: 100%;
        vertical-align: middle;
        height: auto;
        margin: 0 0 30px;
        border-radius: 8px;
      }
      
      p {
        margin: 0 0 16px;
        text-align: left;
      }
      
      p:last-child {
        margin: 0;
      }
      
      a {
        text-decoration: underline;
        color: #327388;
      }
      
      ul {
        margin: 0;
        padding: 0;
        list-style-position: inside;
      }
      
      ul li {
        margin: 0 0 2px;
      }
      
      ul li:last-child {
        margin: 0 0 2px;
      }
    }
  }
  
  @media (max-width: 767px) {
    max-height: 400px;
    overflow-y: scroll;
  }
`;

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  stepType: 'pleatType' | 'lining' | 'measurement' | 'stack';
};

export const CurtainHelpModal = ({
  modalIsOpen,
  closeModal,
  stepType,
}: Props) => {
  const getModalContent = () => {
    let content = '';
    switch (stepType) {
      case 'pleatType':
        content = pleatTypeContent;
        break;
      case 'lining':
        content = liningContent;
        break;
      case 'measurement':
        content = measurementContent;
        break;
      case 'stack':
        content = stackContent;
        break;
      default:
        return '<div>No help content available for this step.</div>';
    }
    
    // Replace image paths with Shopify asset URLs
    return content.replace(/\/images\/curtain\/([^"']+)/g, (match, filename) => {
      // For Shopify, we need to use the asset_url filter
      // Since this is React, we'll use a relative path that works in both dev and production
      return `/assets/${filename}`;
    });
  };

  const htmlContent = getModalContent();

  const customStyles = {
    overlay: {
      zIndex: 1063,
      backgroundColor: 'rgb(36,35,37, 0.25)',
    },
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      width: 'calc(100% - 4rem)',
      maxWidth: '800px',
      transform: 'translate(-50%, -50%)',
      border: '3px solid #327388',
      borderRadius: '10px',
      padding: '20px',
      overflow: 'visible',
    },
  };

  Modal.setAppElement('#root');

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      style={customStyles}
    >
      <StyledWrapper>
        <StyledCloseIconWrapper onClick={closeModal}>
          <CloseIcon />
        </StyledCloseIconWrapper>
        <StyledContent dangerouslySetInnerHTML={{ __html: htmlContent }} />
      </StyledWrapper>
    </Modal>
  );
};
